import { csvLine, esc, slug, stamp } from "./exportApplications";

/**
 * Exports of a diet-and-category's issued membership numbers.
 *
 * This is the register of what was handed out: who got which number, in which
 * series, and whether the chairman has since turned it into a membership. It
 * is taken to be filed and signed — "these are the numbers this sitting
 * issued" — so the listing is ordered by the number itself rather than by name
 * or by the order the screen happened to be sorted in. A roll of numbers that
 * does not run in sequence cannot be checked against the series.
 *
 * SECTIONED by what has actually happened to each one, because the two are
 * different facts and a single list elides them:
 *
 *   · MEMBERS — the number was issued AND the chairman gave final approval, so
 *     the member profile exists and the joining bill was raised;
 *   · AWAITING FINAL APPROVAL — the number is issued and reserved against the
 *     application, and nobody is a member of anything yet.
 *
 * Only ISSUED rows appear at all. An application still waiting on its
 * committee recommendation has no number, and a blank line in a signed roll of
 * numbers is worse than its absence.
 *
 * Built from the SAME rows the screen is showing — the shaped rows of
 * ProbationerMemberNumberController::index — so an export can never disagree
 * with the table it was taken from. Dependency-free in the same way as the
 * module's other exports: CSV with a UTF-8 BOM for Excel, and a styled print
 * window where the browser's own "Save as PDF" does the writing. The shared
 * formatting is imported rather than copied, because two listings of the same
 * sitting that quote or date differently invite the reading that they came
 * from different places.
 */

export type NumberExportMeta = {
  /** The diet the numbers were issued in. */
  diet?: string;
  /** The category, and so the series — Student, Probationer, Technician. */
  category?: string;
  /** The series' own label, when the screen knows it. */
  series?: string;
};

const COLUMNS = [
  "#",
  "Membership No",
  "Applicant",
  "Email",
  "Category",
  "Series",
  "Chapter",
  "Approved On",
  "Status",
] as const;

/** Issued only — anything without a number has nothing to report here. */
const issuedOnly = (rows: any[]) => rows.filter((r) => !!r?.membership_no);

/**
 * Ordered by the NUMBER, numerically.
 *
 * As text "S00009" sorts above "S00010"'s neighbours well enough, but a series
 * whose width has ever changed, or whose numbers carry a postfix, does not
 * sort as text at all. The digits are what the roll is checked against, so the
 * digits are what it is ordered by.
 */
const bySequence = (rows: any[]) =>
  [...rows].sort((a, b) => {
    const seq = (r: any) => {
      const m = String(r?.membership_no ?? "").match(/\d+/);
      return m ? Number(m[0]) : Number.MAX_SAFE_INTEGER;
    };
    return seq(a) - seq(b) || String(a?.name ?? "").localeCompare(String(b?.name ?? ""));
  });

function sectionsOf(rows: any[]) {
  const issued = bySequence(issuedOnly(rows));

  return {
    issued,
    blocks: [
      {
        title: "Members",
        note: "Final approval given — member profile created and joining bill raised.",
        rows: issued.filter((r) => r.final_approved),
      },
      {
        title: "Awaiting final approval",
        note: "Number issued and reserved against the application. Not yet a member.",
        rows: issued.filter((r) => !r.final_approved),
      },
    ],
  };
}

const statusOf = (row: any) =>
  row?.final_approved ? "Member" : "Awaiting final approval";

function toRow(row: any, index: number): (string | number)[] {
  return [
    index + 1,
    row?.membership_no ?? "",
    row?.name ?? "",
    row?.email ?? "",
    row?.category ?? "",
    row?.series_label ?? "",
    row?.chapter ?? "",
    row?.approved_at ?? "",
    statusOf(row),
  ];
}

function heading(meta: NumberExportMeta): string {
  const what = meta.category
    ? `${meta.category} Membership Numbers`
    : "Membership Numbers";

  return meta.diet ? `${what} — ${meta.diet}` : what;
}

function filename(meta: NumberExportMeta, extension: string) {
  const parts = [
    "membership-numbers",
    meta.category ? slug(meta.category) : null,
    meta.diet ? slug(meta.diet) : null,
    stamp().file,
  ].filter(Boolean);

  return `${parts.join("-")}.${extension}`;
}

/** The first and last number in the roll, which is how a block is checked. */
function rangeOf(rows: any[]): string {
  if (!rows.length) return "—";
  const first = rows[0]?.membership_no ?? "";
  const last = rows[rows.length - 1]?.membership_no ?? "";
  return first === last ? String(first) : `${first} — ${last}`;
}

/* ── Excel (CSV) ─────────────────────────────────────────────────────────── */

export function exportMemberNumbersSpreadsheet(rows: any[], meta: NumberExportMeta) {
  const { issued, blocks } = sectionsOf(rows);

  const lines: string[] = [
    csvLine([`NIQS — ${heading(meta)}`]),
    ...(meta.diet ? [csvLine([`Diet: ${meta.diet}`])] : []),
    ...(meta.category ? [csvLine([`Category: ${meta.category}`])] : []),
    ...(meta.series ? [csvLine([`Series: ${meta.series}`])] : []),
    csvLine([`Numbers issued: ${issued.length}`]),
    csvLine([`Range: ${rangeOf(issued)}`]),
    ...blocks.map((b) => csvLine([`${b.title}: ${b.rows.length}`])),
    csvLine([`Taken on: ${stamp().printed}`]),
  ];

  for (const block of blocks) {
    lines.push("", csvLine([`${block.title.toUpperCase()} (${block.rows.length})`]));

    if (!block.rows.length) {
      lines.push(csvLine([`None.`]));
      continue;
    }

    lines.push(
      csvLine([block.note]),
      csvLine([`Range: ${rangeOf(block.rows)}`]),
      csvLine([...COLUMNS]),
      // Numbered WITHIN the block: each is a roll read on its own.
      ...block.rows.map((r, i) => csvLine(toRow(r, i))),
    );
  }

  // The BOM is what makes Excel read this as UTF-8 rather than the local
  // codepage — without it any accented name arrives as mojibake.
  const blob = new Blob(["﻿" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename(meta, "csv");
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick — Safari cancels the download if the URL dies
  // before it has read it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ── PDF (via the browser's print dialog) ────────────────────────────────── */

export function exportMemberNumbersPdf(rows: any[], meta: NumberExportMeta) {
  const { issued, blocks } = sectionsOf(rows);
  const { printed } = stamp();

  const blockHtml = blocks
    .map((block) => {
      if (!block.rows.length) {
        return `
          <section>
            <h2>${esc(block.title)} <span class="count">(0)</span></h2>
            <p class="none">None.</p>
          </section>`;
      }

      return `
        <section>
          <h2>${esc(block.title)} <span class="count">(${block.rows.length})</span></h2>
          <p class="note">${esc(block.note)}</p>
          <p class="note"><strong>Range:</strong> ${esc(rangeOf(block.rows))}</p>
          <table>
            <thead>
              <tr>${COLUMNS.map((c) => `<th>${esc(c)}</th>`).join("")}</tr>
            </thead>
            <tbody>
              ${block.rows
                .map(
                  (r, i) =>
                    `<tr>${toRow(r, i)
                      .map((cell, ci) => `<td class="${ci === 1 ? "num" : ""}">${esc(cell)}</td>`)
                      .join("")}</tr>`,
                )
                .join("")}
            </tbody>
          </table>
        </section>`;
    })
    .join("");

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${esc(filename(meta, "pdf"))}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
         margin: 24px; color: #111827; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .meta { font-size: 11px; color: #6b7280; margin-bottom: 18px; line-height: 1.6; }
  .meta strong { color: #111827; }
  section { margin-bottom: 22px; }
  h2 { font-size: 13px; margin: 0 0 2px; text-transform: uppercase; letter-spacing: .06em; }
  h2 .count { color: #6b7280; font-weight: 500; letter-spacing: 0; }
  .note { font-size: 10px; color: #6b7280; margin: 0 0 8px; }
  .none { font-size: 11px; color: #9ca3af; }
  table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  th, td { border: 1px solid #e5e7eb; padding: 5px 7px; text-align: left; vertical-align: top; }
  th { background: #f9fafb; font-size: 9.5px; text-transform: uppercase;
       letter-spacing: .05em; color: #374151; }
  td.num { font-variant-numeric: tabular-nums; font-weight: 700; white-space: nowrap; }
  tr { page-break-inside: avoid; }
  thead { display: table-header-group; }
  @page { size: A4 landscape; margin: 12mm; }
</style>
</head>
<body>
  <h1>NIQS — ${esc(heading(meta))}</h1>
  <div class="meta">
    ${meta.diet ? `<strong>Diet:</strong> ${esc(meta.diet)}<br/>` : ""}
    ${meta.category ? `<strong>Category:</strong> ${esc(meta.category)}<br/>` : ""}
    ${meta.series ? `<strong>Series:</strong> ${esc(meta.series)}<br/>` : ""}
    <strong>Numbers issued:</strong> ${issued.length} &nbsp;·&nbsp;
    <strong>Range:</strong> ${esc(rangeOf(issued))}<br/>
    <strong>Taken on:</strong> ${esc(printed)}
  </div>
  ${blockHtml}
  <script>
    (function () {
      var printed = false;
      function go() { if (printed) return; printed = true; window.print(); }
      if (document.readyState === "complete") { setTimeout(go, 60); }
      else { window.addEventListener("load", function () { setTimeout(go, 60); }); }
    })();
  </script>
</body>
</html>`;

  // A blob URL rather than document.write: a popup blocked by the browser
  // leaves document.write writing into nothing, with no error anybody sees.
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");

  if (!win) {
    URL.revokeObjectURL(url);
    alert(
      "Your browser blocked the print window. Allow pop-ups for this site, then press PDF again.",
    );
    return;
  }

  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
